import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMomentJalaali } from '@mui/x-date-pickers/AdapterMomentJalaali';
import { faIR as faIRPickers } from '@mui/x-date-pickers/locales';
import { useFormik } from 'formik';
import * as yup from 'yup';
import axios from 'axios';
import moment from 'moment-jalaali';
import { debugDate, simpleDateConversion } from '../utils/dateUtils';

import { API_BASE_URL } from '../config';
import { authService } from '../services/authService';
import {
  PAGE_BG_SX,
  HERO_SX,
  HERO_DECOR_1_SX,
  HERO_DECOR_2_SX,
  CARD_SX,
  SECTION_TITLE_SX,
  FORM_ROW_SX,
  FORM_ROW_MULTILINE_SX,
  FORM_LABEL_SX,
  SELECT_FIELD_SX,
  TEXT_FIELD_SX,
  BTN_OUTLINED_SX,
  BTN_CONTAINED_SX,
  FILTER_SECTION_SX,
} from '../styles/reportPageStyles';

const validationSchema = yup.object({
  company: yup.number().required('شرکت الزامی است'),
  product: yup.number().required('محصول الزامی است'),
  documentType: yup.number().required('نوع سند الزامی است'),
  documentNumber: yup.string().required('شماره سند الزامی است'),
  location: yup.number().required('محل الزامی است'),
  shift: yup.number().required('شیفت الزامی است'),
  date: yup.date().required('تاریخ الزامی است'),
  quantity: yup
    .number()
    .required('مقدار الزامی است')
    .min(1, 'مقدار باید بیشتر از 0 باشد'),
  description: yup
    .string()
    .max(500, 'توضیحات نباید بیشتر از 500 کاراکتر باشد'),
});

const DataEntryForm = ({ onSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const editingEntryId = searchParams.get('edit') ? Number(searchParams.get('edit')) : null;
  const isEditing = Boolean(editingEntryId);
  const user = authService.getCurrentUser() || {};
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [showAssay, setShowAssay] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [shifts, setShifts] = useState([]);
  // Units state - fetch from API
  const [units, setUnits] = useState([]);

  // Admin manage dialog state
  const [manageOpen, setManageOpen] = useState(false);
  const [manageType, setManageType] = useState(null);
  const [manageNewName, setManageNewName] = useState('');
  const [manageEditMap, setManageEditMap] = useState({});

  const getManageConfig = (type) => {
    switch (type) {
      case 'companies':
        return { items: companies, setItems: setCompanies, api: 'companies' };
      case 'products':
        return { items: products, setItems: setProducts, api: 'products' };
      case 'documentTypes':
        return { items: documentTypes, setItems: setDocumentTypes, api: 'document-types' };
      case 'locations':
        return { items: locations, setItems: setLocations, api: 'locations' };
      case 'shifts':
        return { items: shifts, setItems: setShifts, api: 'shifts' };
      default:
        return { items: [], setItems: () => {}, api: '' };
    }
  };


  const closeManage = () => {
    setManageOpen(false);
    setManageType(null);
    setManageNewName('');
    setManageEditMap({});
  };

  const handleManageAdd = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showMessage('توکن نامعتبر است. دوباره وارد شوید.', 'error');
        return;
      }
      const { setItems, api } = getManageConfig(manageType);
      if (!manageNewName.trim()) return;
      const res = await axios.post(`${API_BASE_URL}/${api}/`, { name: manageNewName.trim() }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      });
      setItems((prev) => [...prev, res.data]);
      setManageNewName('');
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'خطا در ایجاد مورد';
      console.error('ManageAdd error:', err);
      showMessage(msg, 'error');
    }
  };

  const handleManageUpdate = async (id) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showMessage('توکن نامعتبر است. دوباره وارد شوید.', 'error');
        return;
      }
      const { items, setItems, api } = getManageConfig(manageType);
      const newName = manageEditMap[id];
      if (!newName || !newName.trim()) return;
      const res = await axios.put(`${API_BASE_URL}/${api}/${id}/`, { name: newName.trim() }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      });
      setItems(items.map((it) => (it.id === id ? res.data : it)));
      showMessage('با موفقیت به‌روزرسانی شد', 'success');
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'خطا در به‌روزرسانی';
      console.error('ManageUpdate error:', err);
      showMessage(msg, 'error');
    }
  };

  const handleManageDelete = async (id) => {
    if (!window.confirm('آیا مطمئن هستید که می‌خواهید این مورد را حذف کنید؟')) {
      return;
    }
    
    const token = localStorage.getItem('token');
    const { items, setItems, api } = getManageConfig(manageType);
    try {
      await axios.delete(`${API_BASE_URL}/${api}/${id}/`, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
      const msg = err?.response?.data?.error || 'خطا در حذف';
      showMessage(msg, 'error');
      return;
    }
    setItems(items.filter((it) => it.id !== id));
    // Clear form value if the deleted item was selected
    switch (manageType) {
      case 'companies':
        if (formik.values.company === id) formik.setFieldValue('company', '');
        break;
      case 'products':
        if (formik.values.product === id) formik.setFieldValue('product', '');
        break;
      case 'documentTypes':
        if (formik.values.documentType === id) formik.setFieldValue('documentType', '');
        break;
      case 'locations':
        if (formik.values.location === id) formik.setFieldValue('location', '');
        break;
      case 'shifts':
        if (formik.values.shift === id) formik.setFieldValue('shift', '');
        break;
      default:
        break;
    }
  };


  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isSubmitting, setIsSubmitting] = useState(false);


  // Function to refresh units data
  const refreshUnits = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        Authorization: `Bearer ${token}`,
      };
      const unitsRes = await axios.get(`${API_BASE_URL}/units/`, { headers });
      setUnits(unitsRes.data);
    } catch (error) {
      console.error('Error refreshing units:', error);
    }
  };

  // Function to refresh products data
  const refreshProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        Authorization: `Bearer ${token}`,
      };
      const productsRes = await axios.get(`${API_BASE_URL}/products/`, { headers });
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Error refreshing products:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {
          Authorization: `Bearer ${token}`,
        };

        const [companiesRes, productsRes, documentTypesRes, locationsRes, shiftsRes, unitsRes] =
          await Promise.all([
            axios.get(`${API_BASE_URL}/companies/`, { headers }),
            axios.get(`${API_BASE_URL}/products/`, { headers }),
            axios.get(`${API_BASE_URL}/document-types/`, { headers }),
            axios.get(`${API_BASE_URL}/locations/`, { headers }),
            axios.get(`${API_BASE_URL}/shifts/`, { headers }),
            axios.get(`${API_BASE_URL}/units/`, { headers }),
          ]);

        setCompanies(companiesRes.data);
        setProducts(productsRes.data);
        setDocumentTypes(documentTypesRes.data);
        setLocations(locationsRes.data);
        setShifts(shiftsRes.data);
        setUnits(unitsRes.data);

        if (!isEditing && companiesRes.data.length === 1) {
          formik.setFieldValue('company', companiesRes.data[0].id);
        }

        if (isEditing && editingEntryId) {
          let entryData = location.state?.editEntry;
          if (!entryData || entryData.id !== editingEntryId) {
            const entriesRes = await axios.get(`${API_BASE_URL}/entries/`, { headers });
            entryData = entriesRes.data.find((e) => e.id === editingEntryId);
          }
          if (entryData) {
            const companyId = companiesRes.data.find((c) => c.name === entryData.company)?.id || '';
            const productId = productsRes.data.find((p) => p.name === entryData.product)?.id || '';
            const documentTypeId = documentTypesRes.data.find((dt) => dt.name === entryData.documentType)?.id || '';
            const locationId = locationsRes.data.find((l) => l.name === entryData.location)?.id || '';
            const shiftId = shiftsRes.data.find((s) => s.name === entryData.shift)?.id || '';

            formik.setValues({
              company: companyId,
              product: productId,
              documentType: documentTypeId,
              documentNumber: entryData.documentNumber || '',
              location: locationId,
              shift: shiftId,
              date: entryData.date ? moment(entryData.date) : moment(),
              quantity: entryData.quantity || '',
              unit: entryData.unit || 'کیلوگرم',
              referenceToWaybill: entryData.referenceToWaybill || '',
              description: entryData.description || '',
              assayPercent: entryData.assayPercent || '',
              lockExceptShift: false,
              shutdownReason: entryData.shutdownReason || '',
            });

            const selectedProduct = productsRes.data.find((p) => p.id === productId);
            if (selectedProduct) setShowAssay(!!selectedProduct.requiresAssay);
          }
        } else if (!isEditing) {
          // Generate auto document number for new entries (use server-provided full string)
          try {
            // Get the latest document number from the server
            const latestDocResponse = await axios.get(`${API_BASE_URL}/entries/latest-doc-number/`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
            
            const serverDoc = latestDocResponse.data.latest_number;
            const autoNumber = typeof serverDoc === 'string' && serverDoc.trim() ? serverDoc.trim() : 'Doc-0001';
            formik.setFieldValue('documentNumber', autoNumber);
          } catch (error) {
            // Fallback to sequential numbering starting from 1
            const autoNumber = 'Doc-0001';
            formik.setFieldValue('documentNumber', autoNumber);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.response?.status === 401) {
          showMessage('لطفاً دوباره وارد شوید', 'error');
        } else {
          showMessage('خطا در دریافت اطلاعات از سرور', 'error');
        }
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingEntryId, isEditing]);

  // Add focus event listener to refresh data when user returns to the form
  useEffect(() => {
    const handleFocus = () => {
      refreshProducts();
      refreshUnits();
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const showMessage = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const formik = useFormik({
    initialValues: {
      company: '',
      product: '',
      documentType: '',
      documentNumber: '',
      location: '',
      shift: '',
      date: moment(),
      quantity: '',
      unit: 'کیلوگرم',
      referenceToWaybill: '',
      description: '',
      assayPercent: '',
      lockExceptShift: false,
      shutdownReason: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // Prevent double submission
      if (isSubmitting) {
        return;
      }
      
      setIsSubmitting(true);
      try {
        const selectedShift = shifts.find((s) => Number(s.id) === Number(values.shift));
        const isShutdownShift = selectedShift?.name === 'Shutdown';
        if (isShutdownShift && !values.shutdownReason) {
          showMessage('برای شیفت خاموشی باید یکی از دلایل را انتخاب کنید', 'error');
          setIsSubmitting(false);
          return;
        }

        const token = localStorage.getItem('token');
        // Debug the date before conversion
        debugDate(values.date, 'Form Submit Date');
        
        let formattedDate = null;
        
        // Method 1: Direct moment formatting (most reliable)
        try {
          if (moment.isMoment(values.date)) {
            // Get the actual date object and format it directly
            const dateObj = values.date.toDate();
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');
            formattedDate = `${year}-${month}-${day}`;
            console.log('Method 1 (direct formatting):', formattedDate);
          }
        } catch (error) {
          console.error('Method 1 failed:', error);
        }
        
        // Method 2: Ultra-simple conversion
        if (!formattedDate) {
          try {
            formattedDate = simpleDateConversion(values.date);
            if (formattedDate) {
              console.log('Method 2 (simpleDateConversion):', formattedDate);
            }
          } catch (error) {
            console.error('Method 2 failed:', error);
          }
        }
        
        // Method 3: Force conversion using native Date
        if (!formattedDate) {
          try {
            const nativeDate = new Date(values.date);
            if (!isNaN(nativeDate.getTime())) {
              const year = nativeDate.getFullYear();
              const month = String(nativeDate.getMonth() + 1).padStart(2, '0');
              const day = String(nativeDate.getDate()).padStart(2, '0');
              formattedDate = `${year}-${month}-${day}`;
              console.log('Method 3 (native Date):', formattedDate);
            }
          } catch (error) {
            console.error('Method 3 failed:', error);
          }
        }
        
        // Method 4: Manual date parsing
        if (!formattedDate && typeof values.date === 'string') {
          try {
            // Try to extract year, month, day from the string
            const dateMatch = values.date.match(/(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
            if (dateMatch) {
              const [, year, month, day] = dateMatch;
              formattedDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              console.log('Method 4 (manual parsing):', formattedDate);
            }
          } catch (error) {
            console.error('Method 4 failed:', error);
          }
        }
        
        if (!formattedDate) {
          console.error('All date conversion methods failed. Date value:', values.date);
          showMessage('فرمت تاریخ نامعتبر است. لطفاً یک تاریخ معتبر شمسی انتخاب کنید', 'error');
          return;
        }
        
        console.log('Final converted date:', formattedDate);

        let response;
        if (isEditing) {
          // Update existing entry using the new backend endpoint
          console.log('Updating entry with data:', { ...values, date: formattedDate });
          console.log('Entry ID to update:', editingEntryId);
          
          try {
            // Build backend payload mapping UI fields to API keys
            // Note: document_number is not included as it cannot be changed during editing
            const updatePayload = {
              company_id: values.company || undefined,
              product_id: values.product || undefined,
              document_type_id: values.documentType || undefined,
              location_id: values.location || undefined,
              shift_id: values.shift || undefined,
              date: formattedDate,
              quantity: values.quantity !== '' ? Number(values.quantity) : undefined,
              unit: values.unit || undefined,
              reference_to_waybill: values.referenceToWaybill || undefined,
              description: values.description ?? undefined,
              assay_percent: showAssay && values.assayPercent !== '' ? Number(values.assayPercent) : undefined,
              shutdown_reason: isShutdownShift ? values.shutdownReason : null,
            };

            // Use PUT method with the new backend endpoint
            response = await axios.put(
              `${API_BASE_URL}/entries/${editingEntryId}/`,
              updatePayload,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
                timeout: 10000,
              }
            );
            console.log('Update response (PUT):', response.data);
            showMessage('اطلاعات با موفقیت ویرایش شد', 'success');
            // Set flag to refresh data display
            localStorage.setItem('entryUpdated', 'true');
          } catch (putError) {
            console.log('PUT failed:', putError.message, putError.response?.status, putError.response?.data);
            if (!putError.response) {
              showMessage('عدم ارتباط با سرور. لطفاً اتصال شبکه یا اجرای بک‌اند را بررسی کنید.', 'error');
            }
            // If PUT fails, try PATCH as fallback
            try {
              const patchPayload = {
                company_id: values.company || undefined,
                product_id: values.product || undefined,
                document_type_id: values.documentType || undefined,
                location_id: values.location || undefined,
                shift_id: values.shift || undefined,
                date: formattedDate,
                quantity: values.quantity !== '' ? Number(values.quantity) : undefined,
                unit: values.unit || undefined,
                reference_to_waybill: values.referenceToWaybill || undefined,
                description: values.description ?? undefined,
                assay_percent: showAssay && values.assayPercent !== '' ? Number(values.assayPercent) : undefined,
                shutdown_reason: isShutdownShift ? values.shutdownReason : null,
              };

              response = await axios.patch(
                `${API_BASE_URL}/entries/${editingEntryId}/`,
                patchPayload,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                  timeout: 10000,
                }
              );
              console.log('Update response (PATCH):', response.data);
              showMessage('اطلاعات با موفقیت ویرایش شد', 'success');
              // Set flag to refresh data display
              localStorage.setItem('entryUpdated', 'true');
            } catch (patchError) {
              console.log('PATCH also failed:', patchError.message, patchError.response?.status, patchError.response?.data);
              if (!patchError.response) {
                showMessage('عدم ارتباط با سرور. لطفاً اتصال شبکه یا اجرای بک‌اند را بررسی کنید.', 'error');
              }
              throw new Error(`خطا در آپدیت: ${patchError.response?.data?.error || patchError.message}`);
            }
          }
        } else {
          // Create new entry
          response = await axios.post(
          `${API_BASE_URL}/entries/`,
          {
            ...values,
            date: formattedDate,
            assayPercent: showAssay && values.assayPercent !== '' ? Number(values.assayPercent) : undefined,
            shutdown_reason: isShutdownShift ? values.shutdownReason : null,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
          showMessage('اطلاعات با موفقیت ثبت شد', 'success');
          
          // Auto reload page after successful submission for new document number
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }

        console.log('Entry saved:', response.data);
        if (isEditing) {
          formik.resetForm();
          localStorage.setItem('entryUpdated', 'true');
          setTimeout(() => navigate(-1), 1500);
        } else {
          formik.resetForm();
          setTimeout(() => {
            if (onSuccess) onSuccess();
          }, 3000);
        }
      } catch (error) {
        console.error('Error saving entry:', error);
        console.error('Error response:', error.response?.data);
        if (error.response?.data?.error) {
          showMessage(error.response.data.error, 'error');
        } else if (error.response?.data?.message) {
          showMessage(error.response.data.message, 'error');
        } else if (error.response?.data) {
          // Handle validation errors
          const errors = error.response.data;
          if (typeof errors === 'object') {
            const errorMessages = Object.values(errors).flat();
            showMessage(`خطا در اعتبارسنجی: ${errorMessages.join(', ')}`, 'error');
          } else {
            showMessage(`خطا: ${errors}`, 'error');
          }
        } else if (error.response?.status === 401) {
          showMessage('لطفاً دوباره وارد شوید', 'error');
        } else if (error.response?.status === 400) {
          showMessage('داده‌های ارسالی نامعتبر است', 'error');
        } else if (error.response?.status === 404) {
          showMessage('رکورد مورد نظر یافت نشد', 'error');
        } else {
          showMessage(`خطا در ذخیره اطلاعات: ${error.message}`, 'error');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const lockExceptShift = formik.values.lockExceptShift;
  const selectedShiftForUI = shifts.find((s) => Number(s.id) === Number(formik.values.shift));
  const isShutdownShift = selectedShiftForUI?.name === 'Shutdown';

  return (
    <Box sx={PAGE_BG_SX}>
      <Box sx={HERO_SX}>
        <Box sx={HERO_DECOR_1_SX} />
        <Box sx={HERO_DECOR_2_SX} />
        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'right' }}>
          <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: -0.5 }}>
            {isEditing ? 'ویرایش ورودی' : 'ثبت ورودی جدید'}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
            {isEditing ? 'اطلاعات را ویرایش و ذخیره کنید' : 'اطلاعات را با دقت وارد کنید'}
          </Typography>
        </Box>
      </Box>

      <Card sx={{ ...CARD_SX, mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="subtitle2" sx={{ ...SECTION_TITLE_SX, mb: 2.5 }}>فرم ثبت داده</Typography>
          <Box component="form" onSubmit={formik.handleSubmit} noValidate sx={FILTER_SECTION_SX}>
            <Box sx={{ mb: 2.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={lockExceptShift}
                    onChange={(e) => formik.setFieldValue('lockExceptShift', e.target.checked)}
                  />
                }
                label="قفل کردن فیلدها (بجز شیفت)"
              />
            </Box>
            
            <Box sx={FORM_ROW_SX}>
              <Typography sx={FORM_LABEL_SX}>شرکت</Typography>
              <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                {user.role === 'admin' && (
                  <Button size="small" variant="outlined" onClick={() => navigate('/manage/companies')} sx={{ minWidth: 0, px: 1 }}>
                    مدیریت
                  </Button>
                )}
                <FormControl sx={{ flex: 1 }}>
                      <Select
                        id="company"
                        name="company"
                        displayEmpty
                        value={formik.values.company}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={lockExceptShift || (user.role !== 'admin' && companies.length <= 1)}
                        error={formik.touched.company && Boolean(formik.errors.company)}
                    sx={SELECT_FIELD_SX}
                  >
                    <MenuItem value="" disabled>انتخاب شرکت</MenuItem>
                        {companies.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                  </Box>
            </Box>

            {/* Manage Dialog for admin */}
            <Dialog open={manageOpen} onClose={closeManage} fullWidth maxWidth="sm">
              <DialogTitle>مدیریت لیست</DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="افزودن مورد جدید"
                    value={manageNewName}
                    onChange={(e) => setManageNewName(e.target.value)}
                  />
                  <Button variant="contained" onClick={handleManageAdd}>افزودن</Button>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {manageType && getManageConfig(manageType).items.map((it) => (
                    <Box key={it.id} sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        value={manageEditMap[it.id] ?? it.name}
                        onChange={(e) => setManageEditMap((prev) => ({ ...prev, [it.id]: e.target.value }))}
                      />
                      <Button variant="outlined" onClick={() => handleManageUpdate(it.id)}>ویرایش</Button>
                      <Button color="error" variant="outlined" onClick={() => handleManageDelete(it.id)}>حذف</Button>
                    </Box>
                  ))}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={closeManage}>بستن</Button>
              </DialogActions>
            </Dialog>

            <Box sx={FORM_ROW_SX}>
              <Typography sx={FORM_LABEL_SX}>محصول</Typography>
              <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                {user.role === 'admin' && (
                  <Button size="small" variant="outlined" onClick={() => navigate('/manage/products')} sx={{ minWidth: 0, px: 1 }}>
                    مدیریت
                  </Button>
                )}
                <FormControl sx={{ flex: 1 }}>
                      <Select
                        id="product"
                        name="product"
                        displayEmpty
                        value={formik.values.product}
                        onFocus={() => {
                          // Refresh products when dropdown is opened to get latest assay status
                          refreshProducts();
                        }}
                        onChange={(e) => {
                          formik.handleChange(e);
                          const selected = products.find((p) => p.id === e.target.value);
                          const requires = !!selected?.requiresAssay;
                          setShowAssay(requires);
                          if (!requires) {
                            formik.setFieldValue('assayPercent', '');
                          }
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.product && Boolean(formik.errors.product)}
                        disabled={lockExceptShift}
                    sx={SELECT_FIELD_SX}
                  >
                    <MenuItem value="" disabled>انتخاب محصول</MenuItem>
                        {products.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{p.name}</span>
                          {p.requiresAssay && (
                            <Box sx={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: 0.5,
                              bgcolor: '#e5f4ff', 
                              color: '#0073ea',
                              px: 1, 
                              py: 0.25, 
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: 500
                            }}>
                              <span>عیار دار</span>
                            </Box>
                          )}
                        </Box>
                      </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                  </Box>
            </Box>

            {showAssay && (
              <Box sx={FORM_ROW_SX}>
                <Typography sx={FORM_LABEL_SX}>عیار</Typography>
                <TextField
                  sx={TEXT_FIELD_SX}
                  id="assayPercent"
                  name="assayPercent"
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                  placeholder="درصد عیار را وارد کنید (0 تا 100)"
                  value={formik.values.assayPercent}
                  onChange={formik.handleChange}
                  disabled={lockExceptShift}
                  onWheel={(e) => e.target.blur()}
                />
              </Box>
            )}

            <Box sx={FORM_ROW_SX}>
              <Typography sx={FORM_LABEL_SX}>نوع سند</Typography>
              <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                {user.role === 'admin' && (
                  <Button size="small" variant="outlined" onClick={() => navigate('/manage/document-types')} sx={{ minWidth: 0, px: 1 }}>
                    مدیریت
                  </Button>
                )}
                <FormControl sx={{ flex: 1 }}>
                      <Select
                        id="documentType"
                        name="documentType"
                        displayEmpty
                        value={formik.values.documentType}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    error={formik.touched.documentType && Boolean(formik.errors.documentType)}
                    disabled={lockExceptShift}
                    sx={SELECT_FIELD_SX}
                  >
                    <MenuItem value="" disabled>انتخاب نوع سند</MenuItem>
                        {documentTypes.map((d) => (
                      <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                  </Box>
            </Box>

            <Box sx={FORM_ROW_SX}>
              <Typography sx={FORM_LABEL_SX}>محل</Typography>
              <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                {user.role === 'admin' && (
                  <Button size="small" variant="outlined" onClick={() => navigate('/manage/locations')} sx={{ minWidth: 0, px: 1 }}>
                    مدیریت
                  </Button>
                )}
                <FormControl sx={{ flex: 1 }}>
                      <Select
                        id="location"
                        name="location"
                        displayEmpty
                        value={formik.values.location}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.location && Boolean(formik.errors.location)}
                        disabled={lockExceptShift}
                    sx={SELECT_FIELD_SX}
                  >
                    <MenuItem value="" disabled>انتخاب محل</MenuItem>
                        {locations.map((l) => (
                      <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                  </Box>
            </Box>

            <Box sx={FORM_ROW_SX}>
              <Typography sx={FORM_LABEL_SX}>شماره سند</Typography>
                  <TextField
                sx={{
                  ...TEXT_FIELD_SX,
                  '& .MuiOutlinedInput-root': {
                    ...TEXT_FIELD_SX['& .MuiOutlinedInput-root'],
                    bgcolor: 'rgba(0, 168, 106, 0.05)',
                    '& fieldset': { borderColor: 'rgba(0, 168, 106, 0.3)' },
                  },
                }}
                    id="documentNumber"
                    name="documentNumber"
                placeholder="شماره سند خودکار"
                    value={formik.values.documentNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                error={formik.touched.documentNumber && Boolean(formik.errors.documentNumber)}
                helperText={isEditing ? "شماره سند قابل ویرایش نیست" : "شماره سند به صورت خودکار تولید شده است"}
                InputProps={{
                  readOnly: true,
                }}
                disabled={lockExceptShift}
              />
            </Box>

            <Box sx={FORM_ROW_SX}>
              <Typography sx={FORM_LABEL_SX}>شیفت</Typography>
              <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                {user.role === 'admin' && (
                  <Button size="small" variant="outlined" onClick={() => navigate('/manage/shifts')} sx={{ minWidth: 0, px: 1 }}>
                    مدیریت
                  </Button>
                )}
                <FormControl sx={{ flex: 1 }}>
                      <Select
                        id="shift"
                        name="shift"
                        displayEmpty
                        value={formik.values.shift}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.shift && Boolean(formik.errors.shift)}
                    sx={SELECT_FIELD_SX}
                  >
                    <MenuItem value="" disabled>انتخاب شیفت</MenuItem>
                        {shifts.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name === 'Shutdown' ? 'خاموشی' : s.name}
                      </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                  </Box>
            </Box>

            {isShutdownShift && (
              <Box sx={FORM_ROW_SX}>
                <Typography sx={FORM_LABEL_SX}>علت خاموشی</Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flex: 1, flexDirection: 'column' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.shutdownReason === 'Power Outage'}
                        onChange={() => formik.setFieldValue('shutdownReason', 'Power Outage')}
                      />
                    }
                    label="قطع برق"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.shutdownReason === 'Lack of Raw Materials'}
                        onChange={() => formik.setFieldValue('shutdownReason', 'Lack of Raw Materials')}
                      />
                    }
                    label="کمبود مواد اولیه"
                  />
                </Box>
              </Box>
            )}

            <Box sx={FORM_ROW_SX}>
              <Typography sx={FORM_LABEL_SX}>تاریخ</Typography>
              <Box sx={{ flex: 1 }}>
                  <LocalizationProvider 
                    dateAdapter={AdapterMomentJalaali}
                    localeText={faIRPickers.components.MuiLocalizationProvider.defaultProps.localeText}
                  >
                    <DatePicker
                      value={formik.values.date}
                      onChange={(val) => formik.setFieldValue('date', val)}
                      disabled={lockExceptShift}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={formik.touched.date && Boolean(formik.errors.date)}
                          helperText={formik.touched.date && formik.errors.date}
                          disabled={lockExceptShift}
                        sx={TEXT_FIELD_SX}
                        />
                      )}
                    />
                  </LocalizationProvider>
              </Box>
            </Box>

            <Box sx={FORM_ROW_SX}>
              <Typography sx={FORM_LABEL_SX}>واحد سنجش</Typography>
              <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                {user.role === 'admin' && (
                  <Button size="small" variant="outlined" onClick={() => navigate('/manage/units')} sx={{ minWidth: 0, px: 1 }}>
                    مدیریت
                  </Button>
                )}
                <FormControl sx={{ flex: 1 }}>
                  <Select
                    id="unit"
                    name="unit"
                    value={formik.values.unit}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={lockExceptShift}
                    sx={SELECT_FIELD_SX}
                  >
                    {units.map((u) => (
                      <MenuItem key={u.id} value={u.name}>{u.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box sx={FORM_ROW_SX}>
              <Typography sx={FORM_LABEL_SX}>مقدار (کیلو)</Typography>
                <TextField
                  sx={TEXT_FIELD_SX}
                  id="quantity"
                  name="quantity"
                  type="number"
                  placeholder="مقدار را به کیلو وارد کنید"
                  value={formik.values.quantity}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onWheel={(e) => e.target.blur()}
                  disabled={lockExceptShift}
                  error={formik.touched.quantity && Boolean(formik.errors.quantity)}
                  helperText={formik.touched.quantity && formik.errors.quantity}
                />
            </Box>

            <Box sx={FORM_ROW_SX}>
              <Typography sx={FORM_LABEL_SX}>ارجاع به حواله</Typography>
              <TextField
                sx={TEXT_FIELD_SX}
                id="referenceToWaybill"
                name="referenceToWaybill"
                placeholder="شماره حواله را وارد کنید (اختیاری)"
                value={formik.values.referenceToWaybill}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                  disabled={lockExceptShift}
              />
            </Box>

            <Box sx={FORM_ROW_MULTILINE_SX}>
              <Typography sx={{ ...FORM_LABEL_SX, mt: 1 }}>توضیحات</Typography>
              <TextField
                fullWidth
                sx={TEXT_FIELD_SX}
                id="description"
                name="description"
                multiline
                minRows={4}
                placeholder="توضیحات را وارد کنید (اختیاری)"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={formik.touched.description && formik.errors.description}
                disabled={lockExceptShift}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: '1px solid #e6e9ef' }}>
              <Button 
                type="submit" 
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  ...BTN_CONTAINED_SX,
                  px: 4,
                  ...(isEditing && { bgcolor: '#00a86a', '&:hover': { bgcolor: '#037f4c' } }),
                }}
              >
                {isSubmitting ? 'در حال ثبت...' : (isEditing ? 'ذخیره تغییرات' : 'ثبت اطلاعات')}
              </Button>
              <Button type="button" variant="outlined" onClick={() => formik.resetForm()} sx={{ ...BTN_OUTLINED_SX, px: 4 }}>
                پاک کردن
              </Button>
              {isEditing && (
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  sx={{ ...BTN_OUTLINED_SX, px: 4, borderColor: '#e44258', color: '#e44258', '&:hover': { borderColor: '#c9364a', bgcolor: 'rgba(228, 66, 88,0.06)' } }}
                >
                  لغو ویرایش
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DataEntryForm; 